import api from './api';

export interface FeaturesResponse {
  features: string[];
  modulos?: string[];
  count: number;
}

class FeaturesService {
  private cache: FeaturesResponse | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 30 * 1000; // 30 segundos para rápida sincronização

  /**
   * Busca features disponíveis do usuário
   * Usa cache para evitar múltiplas requisições
   */
  async getFeatures(forceRefresh: boolean = false): Promise<string[]> {
    const data = await this.fetchFeaturesData(forceRefresh);
    return Array.isArray(data?.features) ? data.features : [];
  }

  /**
   * Busca módulos disponíveis do usuário
   */
  async getModulos(forceRefresh: boolean = false): Promise<string[]> {
    const data = await this.fetchFeaturesData(forceRefresh);
    return Array.isArray(data?.modulos) ? data.modulos : [];
  }

  private inFlightPromise: Promise<FeaturesResponse | null> | null = null;

  private async fetchFeaturesData(forceRefresh: boolean = false): Promise<FeaturesResponse | null> {
    const now = Date.now();

    if (forceRefresh) {
      this.cache = null;
      this.cacheTimestamp = 0;
    } else if (this.cache && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      return this.cache;
    }

    if (this.inFlightPromise) {
      return this.inFlightPromise;
    }

    this.inFlightPromise = (async () => {
      try {
        const response = await api.get<FeaturesResponse>('/features');
        this.cache = response.data;
        this.cacheTimestamp = Date.now();
        return response.data;
      } catch (error) {
        console.error('❌ [FeaturesService] Erro ao buscar features:', error);
        if (this.cache) {
          return this.cache;
        }
        return null;
      } finally {
        this.inFlightPromise = null;
      }
    })();

    return this.inFlightPromise;
  }

  /**
   * Verifica se o usuário tem acesso a uma feature específica
   */
  async hasFeature(featureKey: string): Promise<boolean> {
    const features = await this.getFeatures();
    return features.includes(featureKey);
  }

  /**
   * Verifica se o usuário tem acesso a um módulo oficial específico
   */
  async hasModulo(moduloKey: string): Promise<boolean> {
    const modulos = await this.getModulos();
    return modulos.includes(moduloKey);
  }

  /**
   * Verifica se o usuário tem acesso a pelo menos uma das features
   */
  async hasAnyFeature(featureKeys: string[]): Promise<boolean> {
    const features = await this.getFeatures();
    return featureKeys.some(key => features.includes(key));
  }

  /**
   * Verifica se o usuário tem acesso a todas as features
   */
  async hasAllFeatures(featureKeys: string[]): Promise<boolean> {
    const features = await this.getFeatures();
    return featureKeys.every(key => features.includes(key));
  }

  /**
   * Limpa o cache de features
   */
  clearCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
  }
}

export const featuresService = new FeaturesService();

